import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { ChefHat, ChevronDown, ChevronRight, RotateCcw, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusPill } from "@/components/ui/status-pill";
import { WorkingTablePagination } from "@/components/data-table/working-table-pagination";
import { useAuth } from "@/hooks/useAuth";
import { useCookingSessions } from "@/hooks/cooking-sessions/queries";
import { useUpdateCookingSession } from "@/hooks/cooking-sessions/mutations";
import { formatElapsed } from "@/lib/elapsed";
import type { CookingSession, CookingSessionSource } from "@/types/cooking-session";

const PAGE_SIZE = 5;

/** The wall-clock time a dish came off the pass. */
export function formatFinishedAt(completedAt: string | null): string {
  if (!completedAt) return "—";
  const at = new Date(completedAt);
  if (Number.isNaN(at.getTime())) return "—";
  return at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** How the cook went against the time it was given. */
export function overUnderMinutes(session: Pick<CookingSession, "elapsedMinutes" | "targetMinutes">): number {
  return session.elapsedMinutes - session.targetMinutes;
}

interface CompletedSectionProps {
  locationId?: number;
  source: CookingSessionSource;
}

/**
 * What is already off the pass (T-1108).
 *
 * There was no lane for it: a finished dish stayed in Cooking now wearing a
 * "Done" pill, so the lane the kitchen watches during service filled up with
 * work nobody was doing. It lives here instead, collapsed — it is a record of
 * the shift, not something to keep an eye on.
 *
 * The lane clears at the kitchen's own midnight, which the server works out from
 * the location's timezone (`completedToday`). Without that boundary this would
 * accumulate every dish the kitchen has ever sent.
 */
export function CompletedSection({ locationId, source }: CompletedSectionProps) {
  const { t } = useLingui();
  const { hasPermission } = useAuth();
  const canUpdate = hasPermission("cooking-session:update");
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const updateSession = useUpdateCookingSession();

  const { data, isLoading } = useCookingSessions({
    locationId,
    source,
    status: "COMPLETED",
    completedToday: true,
    sortBy: "completedAt",
    sortOrder: "desc",
    page,
    limit: PAGE_SIZE,
  });

  const sessions = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Putting something back on the line is the one thing needed from here, and
  // it is needed the moment someone marks the wrong dish done.
  async function handleReopen(id: number): Promise<void> {
    try {
      await updateSession.mutateAsync({ id, data: { status: "COOKING" } });
    } catch {
      // error surfaced via updateSession.isError
    }
  }

  return (
    <div className="flex flex-col px-3" data-testid="completed-section">
      <div className="flex items-center justify-between py-3">
        <Button
          variant="ghost"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="h-auto gap-2 px-1 py-1"
        >
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          <span className="text-lg font-semibold text-(--color-text-primary)">{t`Completed`}</span>
          <Badge variant="neutral" className="rounded-full text-xs">
            {isLoading ? "…" : t`${total} today`}
          </Badge>
        </Button>
        <p className="text-xs text-(--color-text-secondary)">
          {t`Clears at the start of the next service day.`}
        </p>
      </div>

      {open && (
        <div className="overflow-hidden rounded-xl border border-(--color-border)">
          <div className="p-2">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="mb-1.5 flex items-center gap-3 rounded-xl bg-(--color-surface-muted) px-3 py-4"
                >
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-20" />
                </div>
              ))
            ) : sessions.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-12 text-(--color-text-secondary)">
                <ChefHat className="size-8" />
                <p className="text-sm">{t`Nothing finished yet today.`}</p>
              </div>
            ) : (
              sessions.map((row) => (
                <CompletedRow
                  key={row.id}
                  row={row}
                  canUpdate={canUpdate}
                  pending={updateSession.isPending}
                  onReopen={handleReopen}
                />
              ))
            )}
          </div>
          {pageCount > 1 && (
            <div className="border-t border-(--color-border) px-3 py-2">
              <WorkingTablePagination page={page} pageCount={pageCount} onPage={setPage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CompletedRow({
  row,
  canUpdate,
  pending,
  onReopen,
}: {
  row: CookingSession;
  canUpdate: boolean;
  pending: boolean;
  onReopen: (id: number) => Promise<void>;
}) {
  const { t } = useLingui();
  const overUnder = overUnderMinutes(row);

  return (
    <div className="mb-1.5 flex items-center gap-3 rounded-xl bg-(--color-surface-muted) px-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-(--color-text-primary)">{row.recipeName}</p>
        <p className="truncate text-xs text-(--color-text-secondary)">
          {[row.chefName, row.stationName].filter(Boolean).join(" · ") || t`Unassigned`}
        </p>
      </div>
      <span className="w-25 shrink-0 text-sm text-(--color-text-secondary)">
        {formatFinishedAt(row.completedAt)}
      </span>
      <span className="w-30 shrink-0 text-sm text-(--color-text-secondary)">
        {/* Against the time it was given, not just how long it took. */}
        {overUnder > 0
          ? t`${formatElapsed(row.elapsedSeconds)} · ${overUnder} min over`
          : t`${formatElapsed(row.elapsedSeconds)} · on time`}
      </span>
      <StatusPill variant="solid" size="lg" status="done">
        {t`Done`}
      </StatusPill>
      <div className="w-25 shrink-0 text-right">
        {canUpdate && (
          <Button
            variant="ghost"
            size="sm"
            disabled={pending}
            onClick={() => void onReopen(row.id)}
            className="gap-1.5"
          >
            {pending ? <Loader2 className="size-3.5 animate-spin" /> : <RotateCcw className="size-3.5" />}
            {t`Back to cooking`}
          </Button>
        )}
      </div>
    </div>
  );
}
